import type { DateRange, DateRangePickerProps } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { isNotNullOrUndefined, vyDate, type VyDate } from '@vy/utils';
import React, { useState } from 'react';

export type VyDateRangeValue = {
  start: Date | null;
  end: Date | null;
};

const DATE_FORMAT = 'MM/DD/YY';

export type VyDateRangePickerProps = Omit<
  DateRangePickerProps,
  'onChange' | 'value' | 'onAccept'
> & {
  value?: VyDateRangeValue;
  onChange: (range: VyDateRangeValue) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
};

export const VyDateRangePicker: React.FC<VyDateRangePickerProps> = ({
  value,
  onChange,
  slotProps,
  label = 'Select date or date range',
  disabled = false,
  error,
  helperText,
  ...dateRangePickerProps
}) => {
  // Use internal state to track selection without triggering parent re-renders
  const [internalValue, setInternalValue] = useState<DateRange<VyDate>>([null, null]);

  const handleDateRangeChange = (newRange: DateRange<VyDate>) => {
    // Only update internal state during selection - don't call parent onChange
    setInternalValue(newRange);
  };

  const handleAccept = (newRange: DateRange<VyDate>) => {
    const [startDayjs, endDayjs] = newRange;

    const start = isNotNullOrUndefined(startDayjs)
      ? vyDate(startDayjs, { keepLocalTime: true }).startOf('day').toDate()
      : null;
    const end = isNotNullOrUndefined(endDayjs)
      ? vyDate(endDayjs, { keepLocalTime: true }).startOf('day').toDate()
      : null;

    // Clear internal state after acceptance to avoid stale values
    setInternalValue([null, null]);

    onChange({ start, end });
  };

  // Compute the display value for the picker
  // When controlled value exists, use it. Otherwise fall back to internal state.
  // Important: Both dates must use the same timezone context to avoid MUI errors.
  const controlledStart = value?.start;
  const controlledEnd = value?.end;

  // For same-day selection (start exists, end is null), we pass [start, start] to MUI
  // to avoid the "MM/DD/YY" placeholder issue, then override the display text
  const isSingleDateSelection = isNotNullOrUndefined(controlledStart) && !isNotNullOrUndefined(controlledEnd);

  const dateRangeValue: DateRange<VyDate> = (() => {
    // If we have a controlled start but no controlled end (same-day selection),
    // pass the same date for both to keep MUI happy with timezone consistency
    if (isNotNullOrUndefined(controlledStart) && !isNotNullOrUndefined(controlledEnd)) {
      const startVyDate = vyDate(controlledStart);
      return [startVyDate, startVyDate];
    }

    // If we have both controlled values, use them
    if (isNotNullOrUndefined(controlledStart) && isNotNullOrUndefined(controlledEnd)) {
      return [vyDate(controlledStart), vyDate(controlledEnd)];
    }

    // Otherwise use internal state (during active selection)
    return internalValue;
  })();

  // Custom display text for single-date selection
  const singleDateDisplayValue =
    isSingleDateSelection && isNotNullOrUndefined(controlledStart)
      ? vyDate(controlledStart).format(DATE_FORMAT)
      : undefined;

  return (
    <DateRangePicker
      value={dateRangeValue}
      onChange={handleDateRangeChange}
      onAccept={handleAccept}
      calendars={2}
      disabled={disabled}
      format={DATE_FORMAT}
      slots={{
        field: SingleInputDateRangeField,
      }}
      slotProps={{
        textField: {
          label,
          fullWidth: true,
          error,
          helperText,
          // Override input value for single-date display
          ...(singleDateDisplayValue && {
            inputProps: { value: singleDateDisplayValue },
          }),
          sx: {
            '& .MuiOutlinedInput-root': {
              background: '#F4FBFF',
              borderRadius: '8px',
              paddingLeft: '8px',
              '& fieldset': {
                borderColor: '#EAF2F7',
              },
              '&:hover fieldset': {
                borderColor: '#EAF2F7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#EAF2F7',
              },
            },
          },
        },
        actionBar: {
          actions: ['cancel', 'accept'],
        },
        ...slotProps,
      }}
      {...dateRangePickerProps}
    />
  );
};
